import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPolicyNoteListByTransaction,
  mapNoteDtoToPolicyNote,
  PolicyNoteDto,
  savePolicyNote,
} from "utils/api/policyNoteApi";
import { PolicyNote } from "entities/UIModel/PolicyManagementRecord";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";

// Loads a transaction's notes from the backend and exposes an add operation.
// Notes edit stays in-session in NotesSection (no PUT endpoint yet).
export function usePolicyNotes(
  policyTransactionId: number | undefined,
  policyNumber?: string | null
) {
  const [{ profileSettings }] = useProfileSettingsCache();
  const userTimeZone = profileSettings?.userTimeZone ?? "";
  const userID = profileSettings?.userID ?? "";
  const userDisplayName = profileSettings?.displayName ?? "";
  const [notes, setNotes] = useState<PolicyNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const reqIdRef = useRef(0);

  const refetch = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    if (!policyTransactionId || !userTimeZone) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    getPolicyNoteListByTransaction({
      PolicyTransactionID: policyTransactionId,
      UserTimeZone: userTimeZone,
    })
      .then((dtos) => {
        if (reqIdRef.current !== myReq) return;
        setNotes(dtos.map(mapNoteDtoToPolicyNote));
        setIsLoading(false);
      })
      .catch(() => {
        if (reqIdRef.current === myReq) setIsLoading(false);
      });
  }, [policyTransactionId, userTimeZone, reloadToken]);

  const addNote = useCallback(
    async (text: string): Promise<boolean> => {
      if (!policyTransactionId || !text.trim()) return false;
      const entity: PolicyNoteDto = {
        PolicyTransactionID: policyTransactionId,
        PolicyNumber: policyNumber ?? null,
        // NOTE_ADDED is the PolicyNoteType for a free-text operator note
        // (SubType GENERAL) per the PolicyNoteType seed in
        // StewartConnect.Databases.PolicyData.
        PolicyNoteTypeCode: "NOTE_ADDED",
        NoteText: text,
        // PolicyNote.NoteEnteredUserID/Name are NOT NULL and the downstream
        // InsertPolicyNoteSingle does not stamp them (it only sets
        // CallingUser/UserID), so send them from the signed-in profile.
        // TODO: ideally the downstream API should stamp these from the token.
        NoteEnteredUserID: userID || null,
        NoteEnteredUserName: userDisplayName || null,
      };
      const result = await savePolicyNote(entity);
      if (result) refetch();
      return !!result;
    },
    [policyTransactionId, policyNumber, userID, userDisplayName, refetch]
  );

  const editNote = useCallback(
    async (policyNoteId: number, text: string): Promise<boolean> => {
      if (!policyNoteId || !text.trim()) return false;
      const ok = await savePolicyNote({
        PolicyNoteID: policyNoteId,
        NoteText: text,
      });
      if (ok) refetch();
      return ok;
    },
    [refetch]
  );

  return { notes, isLoading, refetch, addNote, editNote };
}

export default usePolicyNotes;
